import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { AnimatedSection } from '../components/AnimatedSection';
import {
  Zap,
  Globe,
  PiggyBank,
  Stethoscope,
  Shield,
  Network,
  Sparkles,
  ArrowRight,
  Lock,
  TrendingUp,
  Cpu,
} from 'lucide-react';

export const Home = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: Zap,
      title: 'Quantum Financial System',
      description: 'Next-generation secure financial infrastructure designed for global transactions with quantum-level security.',
      link: '/qfs',
    },
    {
      icon: Globe,
      title: 'Web3 Technology',
      description: 'Decentralized internet technologies including blockchain, digital ownership, and decentralized finance.',
      link: '/web3',
    },
    {
      icon: PiggyBank,
      title: '401(k) Retirement Systems',
      description: 'Advanced retirement savings systems designed to support long-term financial security and growth.',
      link: '/401k',
    },
    {
      icon: Stethoscope,
      title: 'Emerging Medical Technology',
      description: 'Exploring futuristic healthcare innovation and cutting-edge medical technologies.',
      link: '/medbed',
    },
  ];

  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Advanced Security',
      description: 'Military-grade encryption and quantum-resistant security protocols protecting your financial data.',
    },
    {
      icon: Network,
      title: 'Global Financial Connectivity',
      description: 'Seamless integration with global financial networks and decentralized systems worldwide.',
    },
    {
      icon: Sparkles,
      title: 'Future Technology Innovation',
      description: 'Access to emerging technologies and next-generation financial infrastructure.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f]">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxibG9ja2NoYWluJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzZ8MA&ixlib=rb-4.1.0&q=85')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: `translateY(${scrollY * 0.5}px)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A4A] via-transparent to-transparent" />
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FFD700] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#FFD700] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-32">
          <div className="animate-in fade-in slide-in-from-bottom duration-1000">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Future-Ready Financial
              <span className="block text-[#FFD700] mt-2">Innovation</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Exploring next-generation financial infrastructure, decentralized technology, and emerging digital ecosystems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/card-application">
                <Button className="bg-[#FFD700] text-[#0B2A4A] hover:bg-[#FFD700]/90 font-semibold px-8 py-6 text-lg rounded-lg shadow-2xl hover:shadow-[#FFD700]/50 transform hover:scale-105 transition-all duration-300 group">
                  Apply for Card
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/web3">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0B2A4A] font-semibold px-8 py-6 text-lg rounded-lg shadow-xl transform hover:scale-105 transition-all duration-300">
                  Explore Web3
                </Button>
              </Link>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
              <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Overview Section */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Platform Overview
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Our platform explores modern financial technologies, decentralized networks, and evolving global financial infrastructure. We bridge traditional finance with emerging technologies to create unprecedented opportunities.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="group p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Lock className="w-8 h-8 text-[#FFD700]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">Secure Infrastructure</h3>
                <p className="text-gray-600 leading-relaxed">
                  Built on quantum-resistant security protocols and advanced encryption technologies.
                </p>
              </div>

              <div className="group p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-8 h-8 text-[#FFD700]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">Growth Focused</h3>
                <p className="text-gray-600 leading-relaxed">
                  Designed to maximize your financial potential through innovative investment strategies.
                </p>
              </div>

              <div className="group p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Cpu className="w-8 h-8 text-[#FFD700]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">AI-Powered</h3>
                <p className="text-gray-600 leading-relaxed">
                  Leveraging artificial intelligence for predictive analytics and automated optimization.
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Feature Cards Section */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Explore Our Technologies
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover the cutting-edge technologies powering the future of finance
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="group h-full bg-white border-2 border-transparent hover:border-[#FFD700] shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <feature.icon className="w-8 h-8 text-[#FFD700]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-4 group-hover:text-[#FFD700] transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {feature.description}
                      </p>
                      <Link
                        to={feature.link}
                        className="inline-flex items-center text-[#0B2A4A] font-semibold hover:text-[#FFD700] transition-colors group"
                      >
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Why Choose Us Section */}
      <AnimatedSection>
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0B2A4A] rounded-full filter blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Why Choose Us
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience the advantages of next-generation financial technology
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-12">
              {whyChooseUs.map((item, index) => (
                <AnimatedSection key={index} delay={index * 150}>
                  <div className="text-center group">
                    <div className="w-24 h-24 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-xl">
                      <item.icon className="w-12 h-12 text-[#FFD700]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection>
        <section className="py-24 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] relative overflow-hidden">
          <div className="absolute inset-0">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1666816943035-15c29931e975?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxibG9ja2NoYWluJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzZ8MA&ixlib=rb-4.1.0&q=85')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Access the Next Generation of Financial Services
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Join thousands of users already experiencing the future of finance. Apply for your premium card today.
            </p>
            <Link to="/card-application">
              <Button className="bg-[#FFD700] text-[#0B2A4A] hover:bg-[#FFD700]/90 font-bold px-10 py-6 text-lg rounded-lg shadow-2xl hover:shadow-[#FFD700]/50 transform hover:scale-110 transition-all duration-300 group">
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Home;
